import { EmptyState } from "@/components/common/EmptyState";
import { SeatStatusBadge } from "@/components/common/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDateRange, useUsage } from "@/hooks";
import { cn } from "@/lib/utils";
import type { MemberActivity } from "@/types/api";
import { useState } from "react";

const RANGES = [
	{ label: "7 days", days: 7 },
	{ label: "30 days", days: 30 },
	{ label: "90 days", days: 90 },
] as const;

/**
 * Named-employee activity. Unblocked only because the disclosure screen
 * (DisclosureGate) now tells members their organization can see this.
 *
 * ACTIVITY ONLY. The Gateway's analytics layer never exposes questions, notes
 * or the learner's inferred profile, and nothing here should imply otherwise —
 * hence the footnote, which is for the admin's benefit as much as the member's.
 *
 * Every load is audited server-side as `member.viewed`.
 */
export function PeoplePage() {
	const [days, setDays] = useState<number>(30);
	// Stable across renders — see useDateRange. Computing this inline sent the
	// screen into a refetch loop it never came out of.
	const range = useDateRange(days);
	const { data, isLoading } = useUsage(range);

	const s = data?.summary;
	const members = data?.members ?? [];

	return (
		<>
			<PageHeader
				title="People"
				description="How your team is using Curo."
				action={
					<div className="flex rounded-lg border border-divider bg-surface-primary p-0.5">
						{RANGES.map((r) => (
							<button
								key={r.days}
								type="button"
								onClick={() => setDays(r.days)}
								className={cn(
									"rounded-md px-3 py-1 text-caption transition-colors",
									days === r.days
										? "bg-surface3 font-medium text-heading"
										: "text-muted-foreground hover:text-heading",
								)}
							>
								{r.label}
							</button>
						))}
					</div>
				}
			/>

			{isLoading ? (
				<Skeleton className="mb-6 h-24 w-full" />
			) : s ? (
				<Card className="mb-6">
					<CardContent className="flex flex-wrap items-center gap-8 p-5">
						<Stat label="Learning" value={s.learners_active_in_range} />
						<Stat label="Hours" value={Math.round(s.total_minutes / 60)} />
						<Stat label="Tasks touched" value={s.total_tasks_engaged} />
						{/* Never joined is a DIFFERENT problem from joined-and-idle:
						    one needs a nudge, the other needs the seat back. */}
						<Stat
							label="Never joined"
							value={s.never_activated}
							tone={s.never_activated > 0 ? "attention" : "default"}
						/>
					</CardContent>
				</Card>
			) : null}

			{s?.top_topics?.length ? (
				<Card className="mb-6">
					<CardContent className="p-5">
						<h2 className="mb-3 text-body font-semibold text-heading">
							What your team is learning
						</h2>
						<div className="flex flex-wrap gap-2">
							{s.top_topics.map((t) => (
								<span
									key={t.topic}
									className="inline-flex items-center gap-1.5 rounded-full border border-divider bg-surface3 px-2.5 py-1 text-caption text-paragraph"
								>
									{t.topic}
									<span className="tabular text-muted1">{t.learners}</span>
								</span>
							))}
						</div>
					</CardContent>
				</Card>
			) : null}

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : members.length === 0 ? (
				<EmptyState
					title="No one on the team yet"
					description="Invite people from the Seats page. Their activity shows up here once they start learning."
				/>
			) : (
				<Card>
					<div className="overflow-x-auto">
						<table className="w-full text-body">
							<thead>
								<tr className="border-b border-divider text-caption uppercase tracking-wide text-muted-foreground">
									<th className="px-5 py-2.5 text-left font-medium">Person</th>
									<th className="px-5 py-2.5 text-left font-medium">Status</th>
									<th className="px-5 py-2.5 text-right font-medium">
										Active days
									</th>
									<th className="px-5 py-2.5 text-right font-medium">Time</th>
									<th className="px-5 py-2.5 text-right font-medium">
										Completed
									</th>
									<th className="px-5 py-2.5 text-left font-medium">Topics</th>
								</tr>
							</thead>
							<tbody>
								{members.map((m) => (
									<Row key={m.email} m={m} />
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			<p className="mt-4 text-caption text-muted-foreground">
				This view shows learning activity only. Questions asked to Curo, notes,
				and each person's learning profile are private and never shown here.
			</p>
		</>
	);
}

function Row({ m }: { m: MemberActivity }) {
	// Someone who never claimed their seat has no activity to read as "low" —
	// showing zeros would imply disengagement rather than a pending invite.
	const neverJoined = m.seat_status === "invited";
	return (
		<tr className="border-b border-divider last:border-0">
			<td className="px-5 py-3">
				<div className="min-w-0">
					<div className="truncate font-medium text-heading">
						{m.name ?? m.email}
					</div>
					{m.name ? (
						<div className="truncate text-caption text-muted-foreground">
							{m.email}
						</div>
					) : null}
				</div>
			</td>
			<td className="px-5 py-3">
				<SeatStatusBadge status={m.seat_status} />
			</td>
			{neverJoined ? (
				<td
					className="px-5 py-3 text-right text-caption text-muted1"
					colSpan={4}
				>
					Hasn't signed in yet
				</td>
			) : (
				<>
					<td className="tabular px-5 py-3 text-right text-paragraph">
						{m.active_days}
					</td>
					<td className="tabular px-5 py-3 text-right text-paragraph">
						{fmtMinutes(m.minutes_spent)}
					</td>
					<td className="tabular px-5 py-3 text-right text-paragraph">
						{m.tasks_engaged}
					</td>
					<td className="px-5 py-3">
						<span className="line-clamp-1 text-caption text-muted-foreground">
							{m.topics.slice(0, 3).join(", ") || "—"}
						</span>
					</td>
				</>
			)}
		</tr>
	);
}

function Stat({
	label,
	value,
	tone = "default",
}: { label: string; value: number; tone?: "default" | "attention" }) {
	return (
		<div>
			<div
				className={cn(
					"tabular text-h5 font-semibold",
					tone === "attention" ? "text-orange-text" : "text-heading",
				)}
			>
				{value}
			</div>
			<div className="text-caption text-muted-foreground">{label}</div>
		</div>
	);
}

const fmtMinutes = (m: number) =>
	m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
